import React from "react";
import { AlertTriangle, Volume2, Radio } from "lucide-react";
import { useTimezone } from "@/contexts/TimezoneContext";
import RsvpButtons from "./RsvpButtons";
import { countdown, formatWhen, phaseOf } from "./meetingUtils";

function AlertRow({ m, now, tz, onRsvp }) {
  const phase = phaseOf(m, now);
  const active = phase === "soon" || phase === "live";
  return (
    <div
      className={`surface-card border-l-4 ${active ? "border-l-emerald-500" : "border-l-[var(--ff-red)]"} p-4 flex flex-col lg:flex-row lg:items-center gap-4`}
      data-testid={`meeting-alert-${m.id}`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {active ? <Radio size={18} className="text-emerald-400 shrink-0 mt-0.5 animate-pulse" /> : <AlertTriangle size={18} className="text-[var(--ff-red)] shrink-0 mt-0.5" />}
        <div className="min-w-0">
          <div className={`label-eyebrow ${active ? "text-emerald-400" : "text-[var(--ff-red)]"}`} data-testid={`meeting-alert-label-${m.id}`}>
            {phase === "live" ? "▮ MEETING IN PROGRESS" : "▮ REQUIRED ACTION · MEETING SCHEDULED TO START"}
          </div>
          <div className="font-display text-xl truncate mt-1">{m.title}</div>
          <div className="font-mono-ui text-xs text-[var(--ff-text-dim)] mt-1">
            {formatWhen(m.start_at, tz)} · {phase === "live" ? "started" : countdown(m.start_at, now)}
            {!m.my_rsvp && " · Tell us if you can make it"}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {!m.my_rsvp && <RsvpButtons meeting={m} onRsvp={onRsvp} compact />}
        {active && m.room?.join_url && (
          <a href={m.room.join_url} target="_blank" rel="noreferrer" className="btn-primary text-xs" data-testid={`meeting-alert-join-${m.id}`}>
            <Volume2 size={12} /> Join {m.room.channel_name || "VC"}
          </a>
        )}
      </div>
    </div>
  );
}

// Top-of-page banners for invitees: meetings starting soon / live, plus the first unanswered meeting.
export default function MeetingAlert({ data, now, rsvp, onOpen }) {
  const { timezone } = useTimezone();
  if (!data?.can_rsvp) return null;
  const list = data.meetings.filter((m) => phaseOf(m, now) !== "ended" && m.status === "scheduled");
  const active = list.filter((m) => ["soon", "live"].includes(phaseOf(m, now)) && m.my_rsvp?.status !== "unavailable");
  const pending = list.filter((m) => !m.my_rsvp && !active.includes(m));
  if (!active.length && !pending.length) return null;
  return (
    <div className="space-y-3 mb-8" data-testid="meeting-alerts">
      {[...active, ...pending.slice(0, 1)].map((m) => <AlertRow key={m.id} m={m} now={now} tz={timezone} onRsvp={rsvp} />)}
      {pending.length > 1 && (
        <button onClick={onOpen} className="font-mono-ui text-xs uppercase tracking-[0.15em] text-[var(--ff-red)] hover:underline" data-testid="meeting-alert-view-all">
          +{pending.length - 1} more meeting{pending.length > 2 ? "s need" : " needs"} your answer → View meetings
        </button>
      )}
    </div>
  );
}
